import type { TFunction } from 'i18next';
import type { CostVsBudgetChartResponse } from '../../../../api/financials-client/financials-client.type';
import { formatCurrency } from '../../../../helpers/currency-helpers';
import type { CostVersusOutputMeasures } from '../../../../store/financials-store/financials-store.type';
import {
  graphAmethyst,
  graphCambridgeBlue,
  graphChefchaouenBlue,
  graphLightRed,
  graphPacificCyan,
  graphPictonBlue,
  graphSandyBrown,
  graphTropicalIndigo,
} from '../../../../styles/design-tokens';
import type { DataPoint } from '../overview.type';

const measureColorMap = {
  cost: graphTropicalIndigo,
  daily_burn: graphLightRed,
  cost_per_point: graphPictonBlue,
  cost_per_task: graphSandyBrown,
  committed_points: graphChefchaouenBlue,
  completed_points: graphCambridgeBlue,
  committed_tasks: graphAmethyst,
  completed_tasks: graphPacificCyan,
};

/**
 * Gets the measure name from i18n with proper interpolation of work units and estimation.
 *
 * @param measure - The measure key to get the name for
 * @returns The localized measure name
 */
function getMeasureName(measure: CostVersusOutputMeasures, t: TFunction): string {
  // Dynamic key composition — cast `t` to a permissive overload so TFunction's
  // strict key narrowing doesn't reject the runtime-composed namespace path.
  // Same escape-hatch pattern used in process-analysis.helpers.ts.
  return (t as (key: string, opts?: Record<string, unknown>) => string)(`financials.measure_names.${measure}`, {
    work_units: t('common.work_units'),
    work_unit: t('common.work_unit'),
    estimation: t('common.estimation'),
    estimation_singular: t('common.estimation_singular'),
  });
}

/**
 * Transforms the cost versus output data to a format that can be used by the chart.
 *
 * @param {CostVsBudgetChartResponse | undefined} data - The cost versus output data to transform.
 * @returns {DataPoint[]} The transformed cost versus output data.
 */
function transformCostVersusOutputData(data: CostVsBudgetChartResponse | undefined): DataPoint[] {
  if (!data || !data.chart_data) {
    return [];
  }

  return data.chart_data.reduce((dataOverTime: DataPoint[], dataPoint) => {
    // Use label (sprint name) if present, otherwise use the date
    const dateValue = dataPoint.label || new Date(dataPoint.year, dataPoint.month - 1, 1);

    const line: DataPoint = {
      date: dateValue, // Will be string (sprint name) or Date object
      label: dataPoint.label, // Keep label for reference
      committed_points: 0,
      committed_tasks: 0,
      completed_points: 0,
      completed_tasks: 0,
      cost: 0,
      cost_per_point: 0,
      cost_per_task: 0,
      daily_burn: 0,
    };
    dataPoint.projects.forEach((projectData) => {
      line.committed_points += projectData.committed_points;
      line.committed_tasks += projectData.committed_tasks;
      line.completed_points += projectData.completed_points;
      line.completed_tasks += projectData.completed_tasks;
      line.cost += projectData.cost;
      line.daily_burn += projectData.daily_burn;
    });
    line.cost_per_point = line.completed_points > 0 ? Math.round(line.cost / line.completed_points) : 0;
    line.cost_per_task = line.completed_tasks > 0 ? Math.round(line.cost / line.completed_tasks) : 0;
    dataOverTime.push(line);
    return dataOverTime;
  }, []);
}

/**
 * Builds trendLines for all numeric keys in the dataset using least squares regression.
 *
 * @param {DataPoint[]} data Array of DataPoint objects, each with a date and multiple metrics
 * @returns {DataPoint[]} The same data with added trendLine values (key_trend)
 */
function buildTrendLines(data: DataPoint[]): DataPoint[] {
  if (!data || data.length <= 1) {
    return data;
  }

  // Clone the data to avoid modifying the original
  const result = JSON.parse(JSON.stringify(data));

  // Get all numeric keys except 'date' and 'label'
  const keys = Object.keys(data[0]).filter((key) => {
    return key !== 'date' && key !== 'label' && typeof data[0][key as keyof DataPoint] === 'number';
  });

  // Convert dates to numerical values (days since first date) for calculation
  // Handle both Date objects and string sprint names
  const firstDateValue = typeof data[0].date === 'string' ? 0 : new Date(data[0].date).getTime();
  const xValues = data.map((point, index) => {
    // If date is a string (sprint name), use index as x value
    if (typeof point.date === 'string') {
      return index;
    }
    // Otherwise, calculate days since first date
    return (new Date(point.date).getTime() - firstDateValue) / (24 * 60 * 60 * 1000);
  });

  // For each key, calculate and add the trendLine
  keys.forEach((key) => {
    // Get y values for current key
    const yValues = data.map((point) => point[key as keyof DataPoint] as number);

    // Calculate mean of x and y values
    const xMean = xValues.reduce((sum, x) => sum + x, 0) / xValues.length;
    const yMean = yValues.reduce((sum, y) => sum + y, 0) / yValues.length;

    // Calculate slope (m) and y-intercept (b) using least squares method
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < data.length; i++) {
      const xDiff = xValues[i] - xMean;
      const yDiff = yValues[i] - yMean;
      numerator += xDiff * yDiff;
      denominator += xDiff * xDiff;
    }

    const slope = denominator !== 0 ? numerator / denominator : 0;
    const intercept = yMean - slope * xMean;

    // Generate trendLine values
    const trendLineKey = `${key}_trend`;
    for (let i = 0; i < result.length; i++) {
      result[i][trendLineKey] = Math.round(slope * xValues[i] + intercept);
    }
  });

  return result;
}

/**
 * Formats a chart tooltip value with appropriate units based on the measure type.
 * Used for interactive chart tooltips to display values with correct units (currency, points, tasks).
 *
 * @param {number} value - The numeric value to be formatted
 * @param {string} dataKey - The data key identifying the measure type
 * @param {string} currencyCode - The ISO currency code to use for formatting.
 * @returns {string} The formatted value with appropriate units
 */
function formatChartTooltipValue(value: number, dataKey: string, currencyCode: string, t: TFunction): string {
  if (isNaN(value)) {
    return '--';
  }

  switch (dataKey) {
    case 'cost':
    case 'daily_burn':
    case 'cost_per_point':
    case 'cost_per_task':
      return formatCurrency(value, { currency: currencyCode });
    case 'committed_points':
    case 'completed_points':
      return `${value} ${t('common.estimation').toLowerCase()}`;
    case 'committed_tasks':
    case 'completed_tasks':
      return `${value} ${t('common.work_units').toLowerCase()}`;
    default:
      return String(value);
  }
}

export { buildTrendLines, formatChartTooltipValue, getMeasureName, measureColorMap, transformCostVersusOutputData };
